import React, { useEffect, useState } from 'react';
import {
    Container, Typography, Grid, Box, Paper, TextField, Button,
    CircularProgress, Tooltip, Snackbar, Alert, MenuItem, Tabs, Tab,
    Card, CardContent, Divider, Chip, FormControlLabel, Switch
} from '@mui/material';
import {
    Business, Schedule, Place, Mail, Person, Search, Send, 
    Lock, Assignment, HelpOutline, Feedback, Timeline, Reply, Download, Phone
} from '@mui/icons-material';
import { getDatabase, ref, push, get, update } from 'firebase/database';
import TitleSection from '../utils/TittleTheme';
import SidebarNavigation from '../utils/SideBarNavigation';
import firebaseApp, { KEY_REF } from '../adm/admin/firebaseConfig';
import { getConfig } from '../adm/ConfigContext';

const OuvidoriaPage = () => {
    const config = getConfig();
    const [currentTab, setCurrentTab] = useState(0); // 0 = Nova Manifestação / Consulta, 1 = Informações Institucionais

    // Estados do Formulário Profissional
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        type: 'Reclamação', // Denúncia, Reclamação, Sugestão, Elogio, Solicitação
        subject: '',
        message: ''
    });

    const [protocolFilter, setProtocolFilter] = useState('');
    const [generatedProtocol, setGeneratedProtocol] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [success, setSuccess] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchResult, setSearchResult] = useState({ found: false, message: '' });

    // Estados para Parâmetros Institucionais (PNTP 14.1)
    const [ouvidoriaParams, setOuvidoriaParams] = useState(null);

    // Estado para Interação do Cidadão na Timeline
    const [interactionText, setInteractionText] = useState('');
    const [sendingInteraction, setSendingInteraction] = useState(false);

    const databaseInstance = getDatabase(firebaseApp);
    const ouvidoriaRef = ref(databaseInstance, `ouvidoria`);

    const manifestationTypes = [
        { value: 'Denúncia', label: 'Denúncia (Comunicação de irregularidades ou infrações)' },
        { value: 'Reclamação', label: 'Reclamação (Demonstração de insatisfação com serviço público)' },
        { value: 'Sugestão', label: 'Sugestão (Ideia ou proposta de melhoria das ações municipais)' },
        { value: 'Elogio', label: 'Elogio (Demonstração de reconhecimento ou satisfação)' },
        { value: 'Solicitação', label: 'Solicitação (Pedido de adoção de providências ou serviços)' }
    ];

    const calculateTimeLeft = () => {
        const lastSent = localStorage.getItem('ouvidoriaLastSent');
        if (!lastSent) return true;

        const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
        const now = new Date().getTime();
        const difference = now - parseInt(lastSent, 10);

        if (difference < threeDaysInMs) {
            const remaining = threeDaysInMs - difference;
            const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
            const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
            setTimeLeft(`${days} dias e ${hours} horas`);
            return false;
        }
        return true;
    };

    useEffect(() => {
        calculateTimeLeft();
        fetchOuvidoriaParameters();
    }, []);

    // Busca parâmetros institucionais do PNTP item 14.1
    const fetchOuvidoriaParameters = async () => {
        try {
            const paramsRef = ref(databaseInstance, 'config_parametros/ouvidoria');
            const snapshot = await get(paramsRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                const hasData = Object.values(data).some(val => val && val.trim() !== "");
                if (hasData) setOuvidoriaParams(data);
            }
        } catch (error) {
            console.error("Erro ao carregar parâmetros da Ouvidoria:", error);
        }
    };

    const generateProtocolNumber = () => {
        const year = new Date().getFullYear();
        const randomNum = Math.floor(100000 + Math.random() * 900000);
        return `OUV-${year}-${randomNum}`;
    };

    const validateForm = () => {
        if (!formData.subject.trim() || formData.message.trim().length < 15) {
            return false;
        }
        if (!isAnonymous) {
            if (!formData.name.trim() || !formData.email.trim()) return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setSearchResult({ found: false, message: "Preencha todos os campos obrigatórios e descreva com detalhes (mín. 15 caracteres)." });
            return;
        }

        if (!calculateTimeLeft()) {
            setSearchResult({ found: false, message: `Aguarde o limite de segurança. Próximo envio disponível em: ${timeLeft}` });
            return;
        }

        setLoading(true);
        const protocol = generateProtocolNumber();
        const currentDate = new Date();

        const newManifestation = {
            ticket: protocol,
            type: formData.type,
            isAnonymous: isAnonymous,
            name: isAnonymous ? "Manifestante Anônimo" : formData.name,
            email: isAnonymous ? "Nao aplicável" : formData.email,
            phone: isAnonymous ? "Não aplicável" : formData.phone,
            subject: formData.subject,
            description: formData.message,
            date: currentDate.toLocaleDateString('pt-BR'),
            year: currentDate.getFullYear(),
            status: "Recebido",
            historico: [
                {
                    data: currentDate.toLocaleString('pt-BR'),
                    titulo: "Manifestação Protocolada",
                    descricao: `O registro do tipo [${formData.type}] foi inserido no sistema com êxito. A Ouvidoria Geral fará a triagem inicial e resposta em até 20 dias configuráveis.`,
                    autor: "Cidadão",
                    grauSigilo: isAnonymous ? "Reservado" : "Publico"
                }
            ]
        };

        try {
            await push(ouvidoriaRef, newManifestation);
            setGeneratedProtocol(protocol);
            localStorage.setItem('ouvidoriaLastSent', new Date().getTime().toString());
            setSuccess(true);
            
            // Reseta formulário
            setFormData({ name: '', email: '', phone: '', type: 'Reclamação', subject: '', message: '' });
            setIsAnonymous(false);
            calculateTimeLeft();
        } catch (error) {
            console.error("Erro ao salvar manifestação:", error);
            setSearchResult({ found: false, message: "Erro de conexão ao banco de dados." });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchProtocol = () => {
        if (!protocolFilter.trim()) return;
        setSearching(true);
        setSelectedRequest(null);

        get(ouvidoriaRef)
            .then((snapshot) => {
                let foundItem = null;
                snapshot.forEach((childSnapshot) => {
                    const data = childSnapshot.val();
                    if (data.ticket?.toString().trim() === protocolFilter.trim()) {
                        foundItem = { id: childSnapshot.key, ...data };
                    }
                });
                setSearching(false);

                if (foundItem) {
                    if (!foundItem.historico) {
                        foundItem.historico = [
                            { data: foundItem.date, titulo: "Manifestação Criada", descricao: foundItem.description, autor: "Cidadão" }
                        ];
                    }
                    setSelectedRequest(foundItem);
                    setSearchResult({ found: true, message: "Protocolo localizado!" });
                } else {
                    setSearchResult({ found: false, message: "Nenhum protocolo encontrado com esse número." });
                }
            })
            .catch((error) => {
                console.error("Erro na busca:", error);
                setSearching(false);
                setSearchResult({ found: false, message: "Erro ao realizar busca." });
            });
    };

    const handleSendInteraction = async () => {
        if (!interactionText.trim()) return;

        setSendingInteraction(true);
        try {
            const updatedHistory = [...(selectedRequest.historico || [])];
            updatedHistory.push({
                data: new Date().toLocaleString('pt-BR'),
                titulo: "Manifestação complementar do cidadão",
                descricao: interactionText,
                autor: "Cidadão",
                grauSigilo: selectedRequest.isAnonymous ? "Reservado" : "Publico"
            });

            const targetRef = ref(databaseInstance, `ouvidoria/${selectedRequest.id}`);
            await update(targetRef, {
                status: "Complementada",
                historico: updatedHistory
            });

            setSelectedRequest(prev => ({
                ...prev,
                status: "Complementada",
                historico: updatedHistory
            }));

            setInteractionText('');
            setSearchResult({ found: true, message: "Complemento enviado para a Ouvidoria com sucesso!" });
        } catch (error) {
            console.error("Erro ao registrar réplica:", error);
            setSearchResult({ found: false, message: "Erro ao processar envio do complemento." });
        } finally {
            setSendingInteraction(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 4 }}>
                <TitleSection title={`Ouvidoria Geral`} subtitle="Canal direto de diálogo e transparência ativa entre você e o Município" />

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)} indicatorColor="primary" textColor="primary">
                        <Tab label="Registrar & Consultar Manifestação" icon={<Feedback />} iconPosition="start" />
                        <Tab label="Informações e Atendimento Presencial" icon={<Business />} iconPosition="start" />
                    </Tabs>
                </Box>

                {currentTab === 0 ? (
                    <>
                        {/* Formulário Principal de Ouvidoria */}
                        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap">
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Faça o seu Registro Online</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch 
                                            checked={isAnonymous} 
                                            onChange={(e) => setIsAnonymous(e.target.checked)} 
                                            color="warning"
                                        />
                                    }
                                    label={<Box display="flex" alignItems="center" gap={0.5}><Lock fontSize="small" color={isAnonymous ? 'warning' : 'disabled'} /> Quero registrar em Anônimo</Box>}
                                />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Tipo de Manifestação"
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        {manifestationTypes.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                {!isAnonymous && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label="Seu Nome Completo *" 
                                                value={formData.name} 
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label="E-mail para Recebimento de Respostas *" 
                                                value={formData.email} 
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField 
                                                fullWidth 
                                                label="Telefone / WhatsApp (Opcional)" 
                                                value={formData.phone} 
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                                            />
                                        </Grid>
                                    </>
                                )}

                                {isAnonymous && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                            <strong>Modo Anônimo Ativado:</strong> Seus dados pessoais não serão coletados. Certifique-se de salvar o número de protocolo gerado ao final, pois ele será o <strong>único</strong> meio para acompanhar a resposta da prefeitura.
                                        </Alert>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth 
                                        label="Assunto Resumido *" 
                                        value={formData.subject} 
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })} 
                                        placeholder="Ex: Falta de Iluminação na Rua Principal / Elogio ao posto de saúde"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Texto Detalhado da Manifestação *"
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        placeholder="Forneça o máximo de informações possível: datas, locais exatos, nomes envolvidos para agilizar a apuração oficial."
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        sx={{ backgroundColor: config?.color || '#1976d2', color: 'white', fontWeight: 'bold', px: 3 }}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={24} /> : <Send sx={{ mr: 1, fontSize: 18 }} />}
                                        Protocolar Manifestação
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        {generatedProtocol && (
                            <Paper elevation={2} sx={{ p: 3, mb: 3, borderLeft: '6px solid #2e7d32', borderRadius: 2, bgcolor: '#f4fbf7' }}>
                                <Typography variant="h6" color="success.main" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                                    <Assignment /> Protocolado com Sucesso!
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    Código de Rastreio: <strong style={{ fontSize: '1.25rem', color: '#1976d2' }}>{generatedProtocol}</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    Utilize o formulário abaixo ou guarde esse código para consultar a análise jurídica e administrativa de sua demanda.
                                </Typography>
                            </Paper>
                        )}

                        {/* Sistema de Consulta Online de Protocolo */}
                        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Acompanhar Minha Manifestação</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Insira o número do seu protocolo (ex: OUV-2026-XXXXXX) para consultar andamentos, despachos e a resolução da Ouvidoria.
                            </Typography>
                            <Box display="flex" gap={1}>
                                <TextField
                                    fullWidth
                                    label="Número do Protocolo"
                                    value={protocolFilter}
                                    onChange={(e) => setProtocolFilter(e.target.value)}
                                />
                                <Button variant="contained" onClick={handleSearchProtocol} disabled={searching} sx={{ backgroundColor: config?.color || '#1976d2', px: 4 }}>
                                    {searching ? <CircularProgress size={24} /> : <Search />}
                                </Button>
                            </Box>
                        </Paper>

                        {/* Dossiê Interativo da Manifestação Consultada */}
                        {selectedRequest && (
                            <Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                    <Typography variant="h5" sx={{ color: config?.color || '#1976d2', fontWeight: 'bold' }}>
                                        Dossiê do Registro {selectedRequest.ticket}
                                    </Typography>
                                    <Box sx={{ p: '6px 16px', backgroundColor: '#fff3e0', borderRadius: 2, fontWeight: 'bold', color: '#e65100' }}>
                                        Status: {selectedRequest.status}
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 4, backgroundColor: '#f9f9f9', p: 2, borderRadius: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Manifestante:</strong> {selectedRequest.name}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Data de Entrada:</strong> {selectedRequest.date}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Classificação:</strong> {selectedRequest.type}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Assunto:</strong> {selectedRequest.subject}</Typography>
                                    </Grid>
                                </Grid>

                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Timeline /> Histórico de Análises e Despachos do Ouvidor
                                </Typography>

                                <Box sx={{ pl: 2, borderLeft: '3px solid #e0e0e0', ml: 1, display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                                    {selectedRequest.historico?.map((item, index) => (
                                        <Box key={index} sx={{ position: 'relative' }}>
                                            <Box sx={{
                                                position: 'absolute', left: -25, top: 4, width: 12, height: 12, borderRadius: '50%',
                                                backgroundColor: item.autor === 'Cidadão' ? '#1976d2' : '#e65100'
                                            }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: item.autor === 'Cidadão' ? '#1976d2' : '#e65100' }}>
                                                {item.titulo} — <span style={{ color: 'gray', fontWeight: 'normal' }}>{item.data} ({item.autor})</span>
                                                {item.grauSigilo === 'Reservado' && (
                                                    <Chip label="ANÔNIMO" size="small" color="warning" sx={{ fontSize: '0.6rem', ml: 1, height: 18 }} />
                                                )}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, backgroundColor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
                                                {item.descricao}
                                            </Typography>
                                            {item.arquivoUrl && (
                                                <Button size="small" variant="outlined" startIcon={<Download />} href={item.arquivoUrl} target="_blank" sx={{ mt: 1 }}>
                                                    Visualizar Documento Anexo
                                                </Button>
                                            )}
                                        </Box>
                                    ))}
                                </Box>

                                {/* Réplica/Complementação de Informações */}
                                <Box sx={{ p: 3, backgroundColor: '#fdfdfd', borderRadius: 2, border: '1px solid #e3e3e3' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: config?.color || '#1976d2' }}>
                                        <Reply /> Adicionar Informação Complementar ou Réplica
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Caso precise anexar mais detalhes, responder a uma solicitação do ouvidor ou discordar do parecer final, utilize o campo abaixo.
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Digite aqui novas evidências ou esclarecimentos..."
                                        value={interactionText}
                                        onChange={(e) => setInteractionText(e.target.value)}
                                        sx={{ backgroundColor: '#fff', mb: 2 }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleSendInteraction}
                                        disabled={sendingInteraction || !interactionText.trim()}
                                        sx={{ backgroundColor: config?.color || '#1976d2', color: 'white', fontWeight: 'bold' }}
                                    >
                                        {sendingInteraction ? <CircularProgress size={24} /> : "Enviar Complemento"}
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                    </>
                ) : (
                    /* ABA 1 - INFORMAÇÕES DE ATENDIMENTO EXIGIDAS PELO PNTP (ITEM 14.1) */
                    <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Canais de Atendimento Institucionais</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Caso prefira um atendimento direto, formalize sua manifestação através dos meios físicos oficiais regulamentados do Município.
                        </Typography>

                        {ouvidoriaParams ? (
                            <Grid container spacing={3}>
                                {(ouvidoriaParams.unidadeResponsavel || ouvidoriaParams.responsavelNome) && (
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                            <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                <Person sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Responsabilidade</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{ouvidoriaParams.unidadeResponsavel || 'Ouvidoria Geral do Município'}</Typography>
                                                    {ouvidoriaParams.responsavelNome && (
                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                                            <strong>Ouvidor Responsável:</strong> {ouvidoriaParams.responsavelNome}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {ouvidoriaParams.horarioFuncionamento && (
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                            <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                <Schedule sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Horário de Funcionamento</Typography>
                                                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>{ouvidoriaParams.horarioFuncionamento}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {ouvidoriaParams.enderecoFisico && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                <Place sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Endereço Físico do Balcão de Atendimento</Typography>
                                                    <Typography variant="body2">{ouvidoriaParams.enderecoFisico}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {(ouvidoriaParams.emailResponsavel || ouvidoriaParams.telefoneContato) && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fafafa' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" fontWeight="bold" color="textSecondary" mb={1.5}>Contatos Oficiais</Typography>
                                                <Grid container spacing={2}>
                                                    {ouvidoriaParams.emailResponsavel && (
                                                        <Grid item xs={12} sm={6} display="flex" alignItems="center" gap={1}>
                                                            <Mail sx={{ color: 'gray', fontSize: 20 }} />
                                                            <Typography variant="body2"><strong>E-mail:</strong> {ouvidoriaParams.emailResponsavel}</Typography>
                                                        </Grid>
                                                    )}
                                                    {ouvidoriaParams.telefoneContato && (
                                                        <Grid item xs={12} sm={6} display="flex" alignItems="center" gap={1}>
                                                            <Phone sx={{ color: 'gray', fontSize: 20 }} />
                                                            <Typography variant="body2"><strong>Telefone:</strong> {ouvidoriaParams.telefoneContato}</Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Box py={3} textAlign="center">
                                <HelpOutline color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2" color="textSecondary">
                                    Nenhum parâmetro de atendimento físico configurado no painel administrativo ainda.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>

            <Box sx={{ flex: { xs: 1, md: 1.5 }, minWidth: 250 }}>
                <SidebarNavigation noticia={true} />
            </Box>

            {/* Snackbars de Notificação */}
            <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
                <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Manifestação registrada com êxito! Guarde o número de protocolo gerado na tela.
                </Alert>
            </Snackbar>

            <Snackbar open={searchResult.message !== ''} autoHideDuration={4000} onClose={() => setSearchResult({ ...searchResult, message: '' })}>
                <Alert onClose={() => setSearchResult({ ...searchResult, message: '' })} severity={searchResult.found ? "success" : "error"} sx={{ width: '100%' }}>
                    {searchResult.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default OuvidoriaPage;